/**
 * The one way the CRM refreshes its access token.
 *
 * Every client stack uses this — apiClient's interceptor, and authGuards' wrappers
 * around window.fetch and global axios (which is what authFetch, apiCache, the
 * AuthContext heartbeat and every hand-rolled fetch go through). Before item 11 only
 * apiClient tried to refresh at all; the rest turned the first 401 into a logout.
 *
 * Why one shared function and not one per stack: the server rotates refresh tokens and
 * treats a second use of the same token as theft — it revokes the whole family, which
 * signs the user out everywhere. Two stacks (or two tabs) refreshing at the same moment
 * with the same stored token would trip exactly that. So:
 *   - within a tab, concurrent callers share one in-flight refresh;
 *   - across tabs, the refresh runs under a Web Lock, and whoever gets the lock first
 *     checks whether the stored access token already changed (another tab rotated it
 *     while we waited) and, if so, uses that instead of spending the refresh token.
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';
const LOCK_NAME = 'crm-auth-refresh';

/**
 * 401 codes that mean the session is over, not merely that the access token lapsed.
 * Refreshing would be wrong (a deactivated user must not get a new token) or would
 * mask the fault (a wrong-tenant token "fixed" by a fresh one). Clients sign out.
 */
export const TERMINAL_AUTH_CODES = Object.freeze(['ACCOUNT_DEACTIVATED', 'TENANT_MISMATCH', 'SESSION_ENDED']);

export const isTerminalAuthCode = (code) => TERMINAL_AUTH_CODES.includes(code);

/** The error code from an API error body ({ error: { code } }), or null. */
export const authErrorCode = (body) => {
  if (!body || typeof body !== 'object') return null;
  if (body.error && typeof body.error === 'object' && body.error.code) return body.error.code;
  return body.code || null;
};

/** The token in an `Authorization: Bearer …` value, or null. */
export const bearerToken = (headerValue) => {
  if (typeof headerValue !== 'string') return null;
  const m = /^Bearer\s+(\S+)$/i.exec(headerValue.trim());
  return m ? m[1] : null;
};

const read = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

async function refreshUnderLock(staleAccessToken) {
  // Already rotated — by another tab, or by an earlier refresh in this one.
  const current = read('auth_token');
  if (staleAccessToken && current && current !== staleAccessToken) {
    return current;
  }

  const refreshToken = read('auth_refresh_token');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await axios.post(`${API_URL}/api/auth/refresh`, { refreshToken });
  const data = response?.data?.data;
  if (!response?.data?.success || !data?.accessToken) {
    throw new Error('Token refresh failed');
  }

  localStorage.setItem('auth_token', data.accessToken);
  if (data.refreshToken) {
    localStorage.setItem('auth_refresh_token', data.refreshToken);
  }
  return data.accessToken;
}

let inFlight = null;

/**
 * Get a fresh access token.
 *
 * @param {string|null} staleAccessToken - the token the failed request carried. When
 *   the stored token no longer matches it, the stored one is returned without a call.
 * @returns {Promise<string>} the new access token
 * @throws when the session cannot be refreshed (no/expired/revoked refresh token)
 */
export function refreshAccessToken(staleAccessToken = null) {
  if (!inFlight) {
    const run = () => refreshUnderLock(staleAccessToken);
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    const attempt = locks && typeof locks.request === 'function'
      ? locks.request(LOCK_NAME, { mode: 'exclusive' }, run)
      : run();
    inFlight = Promise.resolve(attempt).finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}
